/**
 * LoginOtpModal — manager-issued login OTP for an OTP-login user (Site Engineer etc.).
 * Generates on open, shows the code with a 5-minute countdown, copy + regenerate.
 * Silent by design: the backend sends no SMS/WhatsApp for manager-issued codes and
 * records every issuance on the target user's activity timeline.
 */
import { useEffect, useRef, useState } from 'react';
import { Modal, Button, Tooltip } from 'antd';
import { Copy, RefreshCw, KeyRound } from 'lucide-react';
import { toast } from 'react-toastify';
import type { User } from '@/types/user';
import { useIssueLoginOtp } from '../hooks/useUsers';

interface LoginOtpModalProps {
  user: User;
  open: boolean;
  onClose: () => void;
}

function useCountdown(expiresAt: string | undefined): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  if (!expiresAt) return '';
  const msLeft = Math.max(0, new Date(expiresAt).getTime() - now);
  const m = Math.floor(msLeft / 60000);
  const s = Math.floor((msLeft % 60000) / 1000);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function LoginOtpModal({ user, open, onClose }: LoginOtpModalProps) {
  const issue = useIssueLoginOtp();
  const data = issue.data;
  const countdown = useCountdown(data?.expiresAt);
  const expired = countdown === '0:00';
  const userId = user._id ?? (user as { id?: string }).id ?? '';

  const issuedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (open && userId && issuedForRef.current !== userId) {
      issuedForRef.current = userId;
      issue.mutate(userId);
    }
  }, [open, userId]);

  const copy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.otp);
      toast.success('OTP copied');
    } catch {
      toast.error('Could not copy — select the code manually');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <span className="flex items-center gap-2">
          <KeyRound size={16} /> Login OTP — {user.name}
        </span>
      }
      destroyOnHidden
    >
      {issue.isPending && <p className="py-6 text-center text-sm text-slate-500">Generating code…</p>}
      {issue.isError && (
        <div className="flex flex-col items-center gap-3 py-4">
          <p className="text-sm text-red-600">
            {(issue.error as Error)?.message || 'Failed to generate login OTP'}
          </p>
          <Button icon={<RefreshCw size={14} />} onClick={() => issue.mutate(userId)}>
            Try again
          </Button>
        </div>
      )}
      {data && (
        <div className="flex flex-col items-center gap-3 py-4">
          <p className="text-sm text-slate-600">
            {data.name} · {data.mobile}
          </p>
          <div className={`font-mono text-4xl tracking-[0.4em] ${expired ? 'text-slate-300 line-through' : ''}`}>
            {data.otp}
          </div>
          <p className="text-sm text-slate-500">{expired ? 'Code expired' : `Expires in ${countdown}`}</p>
          <div className="flex gap-2">
            <Tooltip title="Copy code">
              <Button icon={<Copy size={14} />} onClick={copy} disabled={expired}>
                Copy
              </Button>
            </Tooltip>
            <Button icon={<RefreshCw size={14} />} onClick={() => issue.mutate(userId)} loading={issue.isPending}>
              Regenerate
            </Button>
          </div>
          <p className="mt-2 max-w-xs text-center text-xs text-slate-400">
            In the mobile app, tap "Get OTP" for this number, then enter this code. This action is
            recorded in the user's activity log.
          </p>
        </div>
      )}
    </Modal>
  );
}
